import {
  BoltIcon,
  CalendarIcon,
  UserPlusIcon,
  BookOpenIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline'

const ICON_MAP = {
  bolt: BoltIcon,
  calendar: CalendarIcon,
  'user-plus': UserPlusIcon,
  'book-open': BookOpenIcon,
}

const COLOR_MAP = {
  green: { bg: 'bg-green-50', text: 'text-green-600', icon: 'bg-green-100 text-green-600', ring: 'ring-green-200' },
  blue: { bg: 'bg-blue-50', text: 'text-blue-600', icon: 'bg-blue-100 text-blue-600', ring: 'ring-blue-200' },
  purple: { bg: 'bg-purple-50', text: 'text-purple-600', icon: 'bg-purple-100 text-purple-600', ring: 'ring-purple-200' },
  indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', icon: 'bg-indigo-100 text-indigo-600', ring: 'ring-indigo-200' },
  gray: { bg: 'bg-slate-50', text: 'text-slate-600', icon: 'bg-slate-100 text-slate-600', ring: 'ring-slate-200' },
}

export default function AgentCard({ agent, onToggle, onNavigate, expanded, onExpand }) {
  const Icon = ICON_MAP[agent.icon] || BoltIcon
  const colors = COLOR_MAP[agent.color] || COLOR_MAP.gray
  const isKB = agent.slug === 'knowledge-base'

  return (
    <div className={`bg-white rounded-xl border border-slate-200 overflow-hidden transition-all ${
      agent.enabled ? '' : 'opacity-60'
    }`}>
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.icon}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">{agent.name}</h3>
              <p className="text-xs text-slate-500">{agent.description}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Stats badges */}
            {agent.stats && Object.entries(agent.stats).map(([key, value]) => (
              <span key={key} className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full hidden sm:inline">
                {value} {key.replace('total_', '')}
              </span>
            ))}

            {/* Toggle */}
            <button
              onClick={() => !isKB && onToggle(agent.slug, !agent.enabled)}
              disabled={isKB}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                agent.enabled ? 'bg-brand-600' : 'bg-slate-200'
              } ${isKB ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              title={isKB ? 'Knowledge Base cannot be disabled' : ''}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                agent.enabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>

            {/* Expand */}
            <button
              onClick={() => onExpand(agent.slug)}
              className="p-1 rounded-lg hover:bg-slate-100 text-slate-400"
            >
              {expanded ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Settings */}
      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50 p-4">
          <div className="space-y-3">
            {agent.settings?.confidence_threshold !== undefined && (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Confidence Threshold</label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0.1"
                    max="1.0"
                    step="0.05"
                    value={agent.settings.confidence_threshold}
                    onChange={(e) => {
                      // This is read-only display; actual save happens via AgentSettingsForm
                    }}
                    className="flex-1 accent-brand-600"
                    disabled
                  />
                  <span className="text-sm font-mono text-slate-700 w-10">{agent.settings.confidence_threshold}</span>
                </div>
              </div>
            )}

            {agent.settings?.trigger && (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Trigger Mode</label>
                <span className="text-sm text-slate-700 capitalize">{agent.settings.trigger}</span>
              </div>
            )}

            {/* Navigation links */}
            <div className="flex items-center gap-2 pt-2">
              {agent.slug === 'faq' && onNavigate && (
                <button
                  onClick={() => onNavigate('knowledge-base')}
                  className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                >
                  Manage FAQs →
                </button>
              )}
              {agent.slug === 'lead-capture' && onNavigate && (
                <button
                  onClick={() => onNavigate('lead-settings')}
                  className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                >
                  Lead Capture Settings →
                </button>
              )}
              {agent.slug === 'appointment' && onNavigate && (
                <button
                  onClick={() => onNavigate('calendar')}
                  className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                >
                  View Calendar →
                </button>
              )}
              {agent.slug === 'knowledge-base' && onNavigate && (
                <button
                  onClick={() => onNavigate('knowledge-base')}
                  className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                >
                  Manage Knowledge Base →
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
